export type SymmetryMode = 'vertical' | 'horizontal' | 'quad' | 'translation-vertical' | 'translation-horizontal'
export type Difficulty   = 'easy' | 'medium' | 'hard'

export const GRID_SIZES: Record<Difficulty, { rows: number; cols: number }> = {
  easy:   { rows: 8,  cols: 8  },
  medium: { rows: 12, cols: 12 },
  hard:   { rows: 16, cols: 16 },
}

// Colors shown in the user-facing palette
export const PALETTES: Record<Difficulty, string[]> = {
  easy:   ['#000000', '#FFFFFF'],
  medium: ['#1B4332', '#2E4CB8', '#D4A017', '#C0392B', '#FFFFFF'],
  hard:   ['#1B4332', '#2E4CB8', '#7B2D8E', '#D4A017', '#C0392B', '#E8751A', '#FFFFFF'],
}

// Colors used for procedural generation
const GEN_COLORS: Record<Difficulty, string[]> = {
  easy:   ['#000000', '#FFFFFF'],
  medium: ['#1B4332', '#2E4CB8', '#D4A017', '#C0392B', '#FFFFFF'],
  hard:   ['#1B4332', '#2E4CB8', '#7B2D8E', '#D4A017', '#C0392B', '#E8751A', '#FFFFFF'],
}

const NUM_SEEDS: Record<Difficulty, number> = {
  easy:   4,
  medium: 6,
  hard:   8,
}

// Manhattan-distance Voronoi — produces angular, diamond-shaped pixel-art regions
function voronoiPattern(rows: number, cols: number, colors: string[], numSeeds: number): string[][] {
  const seeds = Array.from({ length: numSeeds }, (_, i) => ({
    r:     Math.random() * (rows - 1),
    c:     Math.random() * (cols - 1),
    color: colors[i % colors.length],
  }))

  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      let minD  = Infinity
      let found = colors[0]
      for (const s of seeds) {
        const d = Math.abs(r - s.r) + Math.abs(c - s.c)
        if (d < minD) { minD = d; found = s.color }
      }
      return found
    }),
  )
}

function buildSolution(givenHalf: string[][], rows: number, cols: number, mode: SymmetryMode): string[][] {
  const sol: string[][] = Array.from({ length: rows }, () => Array(cols).fill('#FFFFFF') as string[])
  const halfRows = rows / 2
  const halfCols = cols / 2

  if (mode === 'vertical') {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < halfCols; c++) {
        const color = givenHalf[r][c]
        sol[r][c]              = color
        sol[r][cols - 1 - c]  = color
      }
    }
  } else if (mode === 'horizontal') {
    for (let r = 0; r < halfRows; r++) {
      for (let c = 0; c < cols; c++) {
        const color = givenHalf[r][c]
        sol[r][c]              = color
        sol[rows - 1 - r][c]  = color
      }
    }
  } else if (mode === 'translation-vertical') {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < halfCols; c++) {
        const color = givenHalf[r][c]
        sol[r][c]            = color
        sol[r][c + halfCols] = color  // copy, not mirror
      }
    }
  } else if (mode === 'translation-horizontal') {
    for (let r = 0; r < halfRows; r++) {
      for (let c = 0; c < cols; c++) {
        const color = givenHalf[r][c]
        sol[r][c]            = color
        sol[r + halfRows][c] = color  // copy, not mirror
      }
    }
  } else {
    for (let r = 0; r < halfRows; r++) {
      for (let c = 0; c < halfCols; c++) {
        const color = givenHalf[r][c]
        sol[r][c]                          = color
        sol[r][cols - 1 - c]              = color
        sol[rows - 1 - r][c]              = color
        sol[rows - 1 - r][cols - 1 - c]  = color
      }
    }
  }
  return sol
}

export interface SymmetryPuzzle {
  rows:    number
  cols:    number
  solution: string[][]
  isGiven:  boolean[][]
}

export function generateSymmetryPuzzle(difficulty: Difficulty, mode: SymmetryMode): SymmetryPuzzle {
  const { rows, cols } = GRID_SIZES[difficulty]
  const colors   = GEN_COLORS[difficulty]
  const numSeeds = NUM_SEEDS[difficulty]
  const halfRows = rows / 2
  const halfCols = cols / 2

  const givenRows = (mode === 'vertical' || mode === 'translation-vertical') ? rows     : halfRows
  const givenCols = (mode === 'horizontal' || mode === 'translation-horizontal') ? cols : halfCols

  const givenHalf = voronoiPattern(givenRows, givenCols, colors, numSeeds)
  const solution  = buildSolution(givenHalf, rows, cols, mode)

  const isGiven: boolean[][] = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      if (mode === 'vertical'   || mode === 'translation-vertical')   return c < halfCols
      if (mode === 'horizontal' || mode === 'translation-horizontal') return r < halfRows
      return r < halfRows && c < halfCols
    }),
  )

  return { rows, cols, solution, isGiven }
}
